import React, { useRef, useContext, useState, useEffect, useLayoutEffect } from "react";
import VirtualNumpadInput from "@root/components/patio-configurator/virtual-numpad-input";
import { PatioContext } from "@root/context/patio-calculator";
import lockBodyScroll from "@helpers/lockBodyScroll";
import InputField from "@_components/input-field";
import Button from "@_components/button";
import addImageDimensions from "@root/utils/addImageDimensions";
import infoIcon from "assets/svgs/info.svg";
import { ReactComponent as CloseIcon } from "assets/svgs/close-sidebar.svg";
import Link from "helpers/link";

const MeasurementsWidget = ({ product, show, close, file }) => {

    const { activeCoordinate, setActiveCoordinate, configuration, setMeasurement, measurements: measurementsValues } = useContext(PatioContext) || {};
    const [measurementIndex, setMeasurementIndex] = useState(0);
    const [value, setValue] = useState(0);
    const [error, setError] = useState(null);
    const measurements = product?.measurements || [];

    let mainImageUrl = product?.images?.edges[0]?.node?.url;
    let inputRef = useRef();
    let imageWrapperRef = useRef();
    let imageContainerRef = useRef();

    lockBodyScroll(show, true);

    useLayoutEffect(() => {

        if (show) {
            const index = activeCoordinate ? measurements.findIndex((item) => item.key === activeCoordinate) : 0;
            setValue(configuration[measurements[index].key] || "");
            setMeasurementIndex(index);
            setActiveCoordinate(measurements[index].key);
        }
    }, [show]);

    useEffect(() => {
        console.log('daata useEffect', imageWrapperRef, imageContainerRef);
        function handleResize() {
            try {
                if (imageWrapperRef.current && imageContainerRef.current) {
                    imageWrapperRef.current.style.width = "unset";
                    imageWrapperRef.current.style.height = "unset";
                    const image = imageWrapperRef.current.querySelector("img");
                    // if (image && image.clientWidth) {
                    //     imageWrapperRef.current.style.width = `${image.clientWidth}px`;
                    // }

                    // Can you resize the image proportionally based on imageWrapperRef width and height, and set them in pixels
                    const wrapperWidth = imageContainerRef.current.clientWidth;
                    const wrapperHeight = imageContainerRef.current.clientHeight;

                    if (image && image.naturalWidth && image.naturalHeight) {
                        const aspectRatio = image.naturalWidth / image.naturalHeight;
                        const newWidth = wrapperWidth;
                        const newHeight = newWidth / aspectRatio;

                        if (newHeight > wrapperHeight) {
                            imageWrapperRef.current.style.width = `${wrapperHeight * aspectRatio}px`;
                            imageWrapperRef.current.style.height = `${wrapperHeight}px`;
                        } else {
                            imageWrapperRef.current.style.width = `${newWidth}px`;
                            imageWrapperRef.current.style.height = `${newHeight}px`;
                        }
                    }

                }
            } catch (error) {
                console.error('Error in handleResize:', error);
            }
        }

        handleResize();
        // const interval = setInterval(() => {
        //     console.log('daata interval');
        //     handleResize();
        // }, 50000);
        window.addEventListener("resize", handleResize);

        return () => {
            // clearInterval(interval);
            window.removeEventListener("resize", handleResize);
        }
    }, [show, measurementIndex]);

    function getNextMeasurementIndex() {
        return Object.values(measurementsValues).findIndex((item) => item.default && item.key !== currentMeasurement().key);
    }

    function currentMeasurement() {
        return measurements[measurementIndex] || {};
    }

    function changeMeasurement(index) {
        setMeasurementIndex(index);
        setActiveCoordinate(measurements[index].key);
        setValue(configuration[measurements[index].key] || "");
        setError(null);
    }

    function isValidMeasurement() {
        const currentMeasurementValue = measurementsValues[currentMeasurement().key];
        if (currentMeasurementValue.default || !currentMeasurementValue.value || currentMeasurementValue.value < 0 || currentMeasurementValue.value > 10000) {
            return false;
        }
        return true;
    }

    function nextMeasurement() {
        if (!isValidMeasurement()) {
            setError({ message: "Please enter a valid measurement" });
            return;
        }

        const nextIndex = getNextMeasurementIndex();

        if (nextIndex !== -1) {
            changeMeasurement(nextIndex);
        } else {
            if (typeof close === "function") {
                setMeasurementIndex(-1);
            }
        }
    }

    function previousMeasurement() {
        if (measurementIndex > 0) {
            changeMeasurement(measurementIndex - 1);
        }
    }

    function handleImageLoad() {
        if (imageWrapperRef.current) {
            imageWrapperRef.current.style.width = "unset";
            const image = imageWrapperRef.current.querySelector("img");
            if (image && image.clientWidth) {
                imageWrapperRef.current.style.width = `${image.clientWidth}px`;
            }
        }
    }

    function handleInputChange(event) {
        const inputValue = event.target.value;
        setValue(inputValue);
        setMeasurement(currentMeasurement().key, +inputValue);
        setError(null);
    }

    function handleInputFocus() {
        setError(null);
    }

    function indexToLetter(index) {
        return String.fromCharCode(65 + index);
    }

    function filledItemsLength() {
        return Object.entries(measurementsValues).filter((item) => !item[1].default && item[0] !== currentMeasurement().key).length;
    }

    function imageMarkupWithCoordinates() {
        return (
            <div className="measurements-widget__image" ref={imageWrapperRef}>
                {mainImageUrl && (
                    <img src={addImageDimensions(mainImageUrl, 800)} alt="Patio Image" className="patio-image" onLoad={handleImageLoad} />
                )}
                <div className="measurements-widget__coordinates">
                    {measurements?.map((measurement, index) => {
                        let text = indexToLetter(index);
                        const style = { left: `${measurement.x * 100}%`, top: `${measurement.y * 100}%` };

                        const active = index === measurementIndex;
                        const defaultValue = measurementsValues[measurement.key]?.default;
                        const filled = !defaultValue;

                        const classes = [
                            `measurements-widget__coordinate`,
                            active && "measurements-widget__coordinate--active",
                            filled && "measurements-widget__coordinate--past",
                            defaultValue && "measurements-widget__coordinate--default",
                        ].filter(Boolean).join(" ");

                        if ((filled || active) && !defaultValue) {
                            text = `${configuration[measurement.key]}''`;
                        }

                        return (
                            <div
                                key={index}
                                className={classes}
                                style={style}
                                onClick={() => changeMeasurement(index)}
                            >
                                <span>{text}</span>
                            </div>
                        )
                    }
                    )}
                </div>

                <div className={"measurements-widget__image-lines"}>
                    {measurements?.map((measurement, index) => {
                        const linePoints = measurement.line_points;
                        if (!linePoints) return null;

                        let color = "#800080";
                        let secondColor = "#fff"
                        if (measurement.key !== currentMeasurement().key) {
                            color = "#4443";
                            secondColor = "#fff3";
                        }

                        const x1 = linePoints.point_one_x * 100;
                        const y1 = linePoints.point_one_y * 100;
                        const x2 = linePoints.point_two_x * 100;
                        const y2 = linePoints.point_two_y * 100;
                        const x3 = linePoints.curve_start_x * 100;
                        const y3 = linePoints.curve_start_y * 100;
                        const x4 = linePoints.curve_end_x * 100;
                        const y4 = linePoints.curve_end_y * 100;



                        // Direction vector
                        const dx = x2 - x1;
                        const dy = y2 - y1;

                        // Normalize perpendicular vector
                        const length = Math.sqrt(dx * dx + dy * dy);
                        const offset = 1.2; // length of the normal line (in % coordinates)
                        const nx = -(dy / length) * offset;
                        const ny = (dx / length) * offset;

                        const classes = [
                            `measurements-widget__image-line`,
                            index === measurementIndex && "measurements-widget__image-line--active",
                            filledItemsLength() > 0 && index < filledItemsLength() && "measurements-widget__image-line--past",
                        ].filter(Boolean).join(" ");

                        const isCurved = measurement.line_points?.isCurved;
                        const startX = x1 * 4;
                        const startY = y1 * 4;
                        const endX = x2 * 4;
                        const endY = y2 * 4;
                        const control1X = x3 * 4;
                        const control1Y = y3 * 4;
                        const control2X = x4 * 4;
                        const control2Y = y4 * 4;


                        const pathData = `M ${startX} ${startY} C ${control1X} ${control1Y}, ${control2X} ${control2Y}, ${endX} ${endY}`;

                        return (
                            <>
                                {isCurved && (
                                    <svg viewBox={`0 0 400 400`} key={index} className={classes}>
                                        <path
                                            d={pathData}
                                            stroke={color}
                                            strokeWidth="2"
                                            strokeDasharray="5,5"
                                            // Add fill="none" to prevent the shape from being filled with black
                                            fill="none"
                                            style={{
                                                fill: "none",
                                            }}
                                        />
                                        <path
                                            d={pathData}
                                            stroke={secondColor}
                                            strokeWidth="2"
                                            strokeDasharray="5,5"
                                            strokeDashoffset="5"
                                            fill="none"
                                            style={{
                                                fill: "none",
                                            }}
                                        />
                                    </svg>
                                )}
                                {
                                    !isCurved && (
                                        <svg viewBox={`0 0 400 400`} key={index} className={classes}>
                                            <line
                                                x1={`${x1}%`}
                                                y1={`${y1}%`}
                                                x2={`${x2}%`}
                                                y2={`${y2}%`}
                                                strokeWidth="2"
                                                stroke={color}
                                                strokeDasharray="5,5"

                                            />

                                            <line
                                                x1={`${x1}%`}
                                                y1={`${y1}%`}
                                                x2={`${x2}%`}
                                                y2={`${y2}%`}
                                                strokeWidth="2"
                                                stroke={secondColor}
                                                strokeDasharray="5,5"
                                                strokeDashoffset="5"

                                            />

                                            <line
                                                x1={`${x1 - nx}%`}
                                                y1={`${y1 - ny}%`}
                                                x2={`${x1 + nx}%`}
                                                y2={`${y1 + ny}%`}
                                                stroke={color}
                                                strokeWidth="2"

                                            />

                                            <line
                                                x1={`${x2 - nx}%`}
                                                y1={`${y2 - ny}%`}
                                                x2={`${x2 + nx}%`}
                                                y2={`${y2 + ny}%`}
                                                stroke={color}
                                                strokeWidth="2"

                                            />
                                        </svg>
                                    )
                                }

                            </>

                        );
                    })}
                </div>
            </div>
        );
    }

    function measurementProgress() {
        let add = currentMeasurement()?.key ? 1 : 0;
        return (
            <>
                <div className="measurements-widget__progress-bar">
                    {measurements?.map((measurement, index) => {
                        const classes = [
                            `measurements-widget__progress-bar-item`,
                            index === filledItemsLength() && "measurements-widget__progress-bar-item--active",
                            index < filledItemsLength() && "measurements-widget__progress-bar-item--past",
                            measurementsValues[measurement.key]?.default && "measurements-widget__coordinate--default",
                        ].filter(Boolean).join(" ");

                        return (
                            <div key={index} className={classes} />
                        );
                    })}
                </div>
                <div className="measurements-widget__progress-text">
                    {filledItemsLength() + add} / {measurements.length}
                </div>
            </>
        );
    }

    if (!show) return null;

    if (measurementIndex === -1) {
        return (
            <div className="measurements-widget measurements-widget--completed">
                <div className="measurements-widget__image-wrapper">
                    {imageMarkupWithCoordinates()}
                </div>

                <div className="measurements-widget__progress">
                    {measurementProgress()}
                </div>

                <div className="measurements-widget__completed">
                    <p>Measurement Completed</p>
                    <Button variant="success" className="measurements-widget__completed-button" onClick={close}>Done</Button>
                </div>

                <div className="measurements-widget__overview">
                    <h2>Overview</h2>
                    <ul>
                        {measurements?.map((measurement, index) => {
                            const value = configuration[measurement.key];
                            return (
                                <li key={index} className="measurements-widget__overview-item">
                                    <span className={"measurements-widget__overview-item-index"}>
                                        {indexToLetter(index)}
                                    </span>
                                    <span className={"measurements-widget__overview-item-name"}>
                                        <span className={"measurements-widget__overview-item-value"}>{value}''</span>
                                        {measurement.name}:
                                    </span>

                                    <span className={"measurements-widget__overview-item-edit"} onClick={() => changeMeasurement(index)}>
                                        Edit
                                    </span>
                                </li>
                            );
                        })}
                    </ul>
                </div>
                <div className="measurements-widget__overview-info">
                    <img src={infoIcon} alt="Info" />
                    To ensure a perfect fit, we’ll add 1-2” leeway to the width and depth.
                    For any curved or angled dimension, find the furthest point and take a straight, level measurement from it.
                </div>
            </div>
        );
    }

    return (
        <div className="measurements-widget">
            <div className="measurements-widget__image-wrapper" ref={imageContainerRef}>
                {imageMarkupWithCoordinates()}
            </div>

            <div className="measurements-widget__progress">
                {measurementProgress()}
            </div>

            {file && (
                <div className="measurements-widget__how-to-measure">
                    <Link to={file} target="_blank" >

                        <img src={infoIcon} alt="Info" />
                        How to measure?

                    </Link>
                </div>
            )}

            <div className="measurements-widget__measurement">
                <label htmlFor="measurement-input" className="measurements-widget__measurement-label">
                    {indexToLetter(measurementIndex)}. {currentMeasurement().name}
                </label>
                <div className="measurements-widget__measurement-row">
                    <div className="measurements-widget__measurement-input-wrapper">
                        <InputField
                            id="measurement-input"
                            inputId="measurement-input--field"
                            className="measurements-widget__measurement-input"
                            placeholder={"Enter measurement"}
                            value={measurementsValues[currentMeasurement().key]?.default ? "" : value}
                            suffix={"inch"}
                            onChange={handleInputChange}
                            onFocus={handleInputFocus}
                            error={error}
                            readOnly={true}
                        />
                    </div>
                    <Button disabled={measurementIndex === 0 ? true : false} className="measurements-widget__measurement-button measurements-widget__measurement-button--prev" onClick={previousMeasurement}>
                        Back
                    </Button>
                    <Button className="measurements-widget__measurement-button measurements-widget__measurement-button--next" onClick={nextMeasurement}>
                        {getNextMeasurementIndex() !== -1 ? 'Next' : 'Finish'}
                    </Button>
                </div>
            </div>

            <div className="measurements-widget__numpad-wrapper">
                <VirtualNumpadInput inputId={"measurement-input--field"} onChange={handleInputChange} />
            </div>
        </div>
    );
}

export default MeasurementsWidget;